import { useRef, useState } from "react";
import { Plus, Loader2 } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@/lib/api";
import { cn } from "@/lib/utils";
import type { Status } from "./directory-data";

export function InlineAddTask({
  status,
  defaults,
}: {
  status: Status;
  defaults?: { vendor_ids?: number[]; person_ids?: number[]; category?: string };
}) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const create = useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      const res = await api(`/api/operations_tasks`, {
        method: "POST",
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { detail?: string; error?: string };
        throw new Error(j.detail || j.error || `HTTP ${res.status}`);
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["operations_tasks"] });
      setTitle("");
      inputRef.current?.focus();
    },
  });

  function submit() {
    const trimmed = title.trim();
    if (!trimmed || create.isPending) return;
    create.mutate({
      title: trimmed,
      status,
      priority: "medium",
      category: defaults?.category ?? "Operations",
      vendor_ids: defaults?.vendor_ids ?? [],
      person_ids: defaults?.person_ids ?? [],
    });
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => {
          setOpen(true);
          requestAnimationFrame(() => inputRef.current?.focus());
        }}
        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1.5 rounded-lg hover:bg-muted/50 w-full"
      >
        <Plus className="w-3.5 h-3.5" />
        Add task
      </button>
    );
  }

  return (
    <div
      className={cn(
        "flex items-center gap-1 bg-card border border-border rounded-lg px-2 py-1.5",
      )}
    >
      <input
        ref={inputRef}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            submit();
          } else if (e.key === "Escape") {
            setTitle("");
            setOpen(false);
          }
        }}
        onBlur={() => {
          if (!title.trim()) setOpen(false);
        }}
        placeholder="Task title — Enter to save, Esc to cancel"
        className="flex-1 bg-transparent text-xs text-foreground placeholder:text-muted-foreground outline-none"
        disabled={create.isPending}
      />
      {create.isPending && (
        <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />
      )}
    </div>
  );
}
